// apps/site-dna-web/src/components/ev/EVLoadChart.js
/**
 * EVLoadChart - Displays EV charging load profile
 *
 * Wrapper around Recharts AreaChart optimized for EV loads.
 *
 * Supports two modes:
 *   1. Simple mode: Single area from `loads` array (existing behavior)
 *   2. Stacked mode: Per-charger stacked areas from `perChargerLoads`
 *      with 5-min resolution and animated playhead
 */

import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    ReferenceLine,
} from 'recharts';
import { vortxColors } from '@vortx/shared-ui';

const STEPS_PER_HOUR = 12; // 5-minute intervals
const TOTAL_STEPS = 24 * STEPS_PER_HOUR; // 288

// Per-charger colors for stacked mode
const CHARGER_COLORS = [
    '#3f6a64', '#b28c50', '#5a9a94', '#7a6a40',
    '#2d524d', '#d4b870', '#4a7a74', '#9a8050',
];
const CHARGER_COLORS_DARK = [
    '#5fd4c4', '#d4a85a', '#4ab8a8', '#c0a060',
    '#38a898', '#e8c080', '#60c0b0', '#b09050',
];

/**
 * Format hour index to time string
 */
function formatHour(hour) {
    const h = hour % 24;
    if (h === 0) return '12 AM';
    if (h === 12) return '12 PM';
    return h < 12 ? `${h} AM` : `${h - 12} PM`;
}

/**
 * Format hour for 48-hour axis (shows Day 1 / Day 2 context)
 */
function formatHour48(hour) {
    const h = hour % 24;
    const dayLabel = hour >= 24 ? 'D2 ' : '';
    if (h === 0) return `${dayLabel}12 AM`;
    if (h === 12) return `${dayLabel}12 PM`;
    return h < 12 ? `${dayLabel}${h} AM` : `${dayLabel}${h - 12} PM`;
}

/**
 * Format 5-min index to time string
 */
function formatFiveMinTime(index) {
    const h = Math.floor(index / STEPS_PER_HOUR);
    const m = (index % STEPS_PER_HOUR) * 5;
    const period = h < 12 ? 'AM' : 'PM';
    let display = h % 12;
    if (display === 0) display = 12;
    return `${display}:${String(m).padStart(2, '0')} ${period}`;
}

function formatAxisTick(index) {
    const h = Math.floor(index / STEPS_PER_HOUR);
    const map = { 0: '12A', 3: '3A', 6: '6A', 9: '9A', 12: '12P', 15: '3P', 18: '6P', 21: '9P' };
    return map[h] || '';
}

/**
 * EVLoadChart Component
 *
 * @param {Object} props
 * @param {Array} props.loads - Array of hourly load values (kW) - simple mode
 * @param {Object} props.perChargerLoads - { 0: [24 hourly values], 1: [...] } - stacked mode
 * @param {number} props.numChargers - Number of chargers (stacked mode)
 * @param {number} props.playheadIndex - Current 5-min time index 0-287 (stacked mode)
 * @param {number} props.peakDemand - Peak demand value for reference line
 * @param {number} props.siteCapacity - Site capacity for reference line
 * @param {string} props.view - 'day' for 24h or 'year' for 8760 (simple mode only)
 * @param {number} props.height - Chart height in pixels
 */
function EVLoadChart({
    loads = [],
    perChargerLoads = null,
    numChargers = 0,
    playheadIndex = null,
    peakDemand = null,
    siteCapacity = null,
    onHoverTime = null,
    view = 'day',
    height = 300,
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const isStacked = perChargerLoads && numChargers > 0;
    const colors = isDark ? CHARGER_COLORS_DARK : CHARGER_COLORS;

    // --- Stacked mode: build 5-min resolution per-charger data ---
    const stackedData = useMemo(() => {
        if (!isStacked) return null;

        const points = [];
        for (let h = 0; h < 24; h++) {
            for (let s = 0; s < STEPS_PER_HOUR; s++) {
                const idx = h * STEPS_PER_HOUR + s;
                const entry = { index: idx };
                for (let c = 0; c < numChargers; c++) {
                    const key = perChargerLoads[c] !== undefined ? c : String(c);
                    const hourly = perChargerLoads[key] || [];
                    entry[`c${c}`] = Math.round((hourly[h] || 0) * 100) / 100;
                }
                points.push(entry);
            }
        }
        return points;
    }, [isStacked, perChargerLoads, numChargers]);

    const { stackedMaxY, stackedYTicks } = useMemo(() => {
        if (!stackedData) return { stackedMaxY: 0, stackedYTicks: [] };
        let max = peakDemand || 0;
        for (const d of stackedData) {
            let total = 0;
            for (let c = 0; c < numChargers; c++) {
                total += d[`c${c}`] || 0;
            }
            if (total > max) max = total;
        }
        if (max <= 0) return { stackedMaxY: 100, stackedYTicks: [0, 25, 50, 75, 100] };

        // Compute nice round tick interval (4-6 ticks)
        const rawInterval = max / 4;
        const magnitude = Math.pow(10, Math.floor(Math.log10(rawInterval)));
        const niceSteps = [1, 2, 2.5, 5, 10];
        const normalized = rawInterval / magnitude;
        let interval = magnitude * niceSteps[niceSteps.length - 1];
        for (const step of niceSteps) {
            if (normalized <= step) { interval = magnitude * step; break; }
        }

        const ceilMax = Math.ceil(max / interval) * interval;
        const ticks = [];
        for (let v = 0; v <= ceilMax; v += interval) {
            ticks.push(Math.round(v));
        }
        return { stackedMaxY: ceilMax, stackedYTicks: ticks };
    }, [stackedData, numChargers, peakDemand]);

    // --- Simple mode: existing behavior ---
    const simpleData = useMemo(() => {
        if (isStacked) return null;
        if (!loads || loads.length === 0) return [];

        if (view === 'day') {
            // Use up to 48 hours if available to show overnight charging
            const numHours = loads.length >= 48 ? 48 : Math.min(loads.length, 24);
            const hours = loads.slice(0, numHours);
            const fmt = numHours > 24 ? formatHour48 : formatHour;
            return hours.map((val, idx) => ({
                hour: idx,
                load: val,
                label: fmt(idx),
            }));
        }

        const sampled = [];
        for (let i = 0; i < loads.length; i += 24) {
            const dayLoads = loads.slice(i, i + 24);
            const maxLoad = Math.max(...dayLoads);
            sampled.push({
                day: Math.floor(i / 24),
                load: maxLoad,
            });
        }
        return sampled;
    }, [isStacked, loads, view]);

    // --- Empty state ---
    if (!isStacked && (!loads || loads.length === 0)) {
        return (
            <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">
                    Run simulation to see load profile
                </Typography>
            </Box>
        );
    }

    // --- Stacked tooltip ---
    const StackedTooltipContent = useMemo(() => {
        return function StackedTooltip({ active, payload, label }) {
            if (!active || !payload || !payload.length) return null;
            const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);

            return (
                <Box
                    sx={{
                        bgcolor: theme.palette.background.paper,
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : vortxColors.stone}`,
                        borderRadius: 1,
                        p: 0.75,
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}
                    >
                        {formatFiveMinTime(label)}
                    </Typography>
                    {payload.map((p, i) => (
                        <Box
                            key={i}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 1.5,
                            }}
                        >
                            <Typography variant="caption" sx={{ color: p.color }}>
                                Charger {i + 1}
                            </Typography>
                            <Typography variant="caption">
                                {(p.value || 0).toFixed(1)} kW
                            </Typography>
                        </Box>
                    ))}
                    <Box
                        sx={{
                            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#eee'}`,
                            mt: 0.25,
                            pt: 0.25,
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1.5,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, color: vortxColors.gold }}
                        >
                            Total
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, color: vortxColors.gold }}
                        >
                            {total.toFixed(1)} kW
                        </Typography>
                    </Box>
                </Box>
            );
        };
    }, [theme, isDark]);

    // X-axis ticks for stacked mode: every 3 hours
    const stackedXTicks = [0, 36, 72, 108, 144, 180, 216, 252];

    const axisColor = isDark ? 'rgba(255,255,255,0.5)' : theme.palette.text.secondary;

    // Chart hover → scrub animation time
    const handleChartMouseMove = (state) => {
        if (onHoverTime && state?.activeTooltipIndex != null) {
            onHoverTime(state.activeTooltipIndex);
        }
    };
    const handleChartMouseLeave = () => {
        if (onHoverTime) onHoverTime(null);
    };

    // --- Stacked rendering ---
    if (isStacked) {
        return (
            <Box sx={{ width: '100%', height }}>
                <ResponsiveContainer>
                    <AreaChart
                        data={stackedData}
                        margin={{ top: 10, right: 12, left: 0, bottom: 0 }}
                        onMouseMove={handleChartMouseMove}
                        onMouseLeave={handleChartMouseLeave}
                    >
                        <defs>
                            {Array.from({ length: numChargers }).map((_, c) => (
                                <linearGradient
                                    key={c}
                                    id={`evCharger${c}`}
                                    x1="0" y1="0" x2="0" y2="1"
                                >
                                    <stop
                                        offset="5%"
                                        stopColor={colors[c % colors.length]}
                                        stopOpacity={0.7}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor={colors[c % colors.length]}
                                        stopOpacity={0.15}
                                    />
                                </linearGradient>
                            ))}
                        </defs>
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke={isDark ? 'rgba(255,255,255,0.08)' : '#eee'}
                        />
                        <XAxis
                            dataKey="index"
                            type="number"
                            domain={[0, TOTAL_STEPS - 1]}
                            ticks={stackedXTicks}
                            tickFormatter={formatAxisTick}
                            stroke={axisColor}
                            style={{ fontSize: 11 }}
                        />
                        <YAxis
                            domain={[0, stackedMaxY]}
                            ticks={stackedYTicks}
                            stroke={axisColor}
                            style={{ fontSize: 11 }}
                            width={48}
                            tickFormatter={(val) =>
                                val >= 1000
                                    ? `${(val / 1000).toFixed(val % 1000 === 0 ? 0 : 1)}k`
                                    : String(Math.round(val))
                            }
                            label={{
                                value: 'kW',
                                angle: -90,
                                position: 'insideLeft',
                                style: { fill: axisColor, fontSize: 11 },
                            }}
                        />
                        <Tooltip content={<StackedTooltipContent />} />

                        {/* Stacked areas per charger */}
                        {Array.from({ length: numChargers }).map((_, c) => (
                            <Area
                                key={c}
                                type="stepAfter"
                                dataKey={`c${c}`}
                                stackId="load"
                                stroke={colors[c % colors.length]}
                                fill={`url(#evCharger${c})`}
                                strokeWidth={1}
                                isAnimationActive={false}
                            />
                        ))}

                        {/* Site capacity reference line */}
                        {siteCapacity && (
                            <ReferenceLine
                                y={siteCapacity}
                                stroke={vortxColors.gold}
                                strokeDasharray="5 5"
                                label={{
                                    value: `Capacity: ${Math.round(siteCapacity)} kW`,
                                    position: 'right',
                                    fill: vortxColors.gold,
                                    fontSize: 10,
                                }}
                            />
                        )}

                        {/* Peak demand reference line */}
                        {peakDemand != null && peakDemand > 0 && (
                            <ReferenceLine
                                y={peakDemand}
                                stroke="#ff6b6b"
                                strokeDasharray="3 3"
                                label={{
                                    value: `Peak ${Math.round(peakDemand)}kW`,
                                    position: 'right',
                                    fill: '#ff6b6b',
                                    fontSize: 9,
                                }}
                            />
                        )}

                        {/* Animated playhead */}
                        {playheadIndex != null && (
                            <ReferenceLine
                                x={playheadIndex}
                                stroke={isDark ? '#ff4444' : '#d32f2f'}
                                strokeWidth={1.5}
                            />
                        )}
                    </AreaChart>
                </ResponsiveContainer>
            </Box>
        );
    }

    // --- Simple rendering (existing behavior) ---
    return (
        <Box sx={{ width: '100%', height }}>
            <ResponsiveContainer>
                <AreaChart data={simpleData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <defs>
                        <linearGradient id="evLoadGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={vortxColors.teal} stopOpacity={0.8} />
                            <stop offset="95%" stopColor={vortxColors.teal} stopOpacity={0.1} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke={isDark ? 'rgba(255,255,255,0.1)' : '#eee'}
                    />
                    <XAxis
                        dataKey={view === 'day' ? 'hour' : 'day'}
                        stroke={theme.palette.text.secondary}
                        style={{ fontSize: 11 }}
                        interval={view === 'day' && simpleData?.length > 24 ? 2 : 0}
                        tickFormatter={view === 'year' ? (d) => {
                            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                            return months[Math.floor(d / 30)] || '';
                        } : view === 'day' ? (h) => {
                            const fmt = simpleData?.length > 24 ? formatHour48 : formatHour;
                            return fmt(h);
                        } : undefined}
                    />
                    <YAxis
                        stroke={theme.palette.text.secondary}
                        style={{ fontSize: 11 }}
                        tickFormatter={(val) => val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val.toFixed(0)}
                        label={{
                            value: 'kW',
                            angle: -90,
                            position: 'insideLeft',
                            style: { fill: theme.palette.text.secondary, fontSize: 11 },
                        }}
                    />
                    <Tooltip
                        formatter={(val) => [`${Math.round(val).toLocaleString()} kW`, 'Load ']}
                        labelFormatter={(h) => view === 'day'
                            ? (simpleData?.length > 24 ? formatHour48(h) : formatHour(h))
                            : h
                        }
                        contentStyle={{
                            borderRadius: 8,
                            border: isDark ? '1px solid rgba(255,255,255,0.1)' : 'none',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                            backgroundColor: theme.palette.background.paper,
                        }}
                    />
                    {siteCapacity && (
                        <ReferenceLine
                            y={siteCapacity}
                            stroke={vortxColors.gold}
                            strokeDasharray="5 5"
                            label={{
                                value: `Capacity: ${Math.round(siteCapacity)} kW`,
                                position: 'right',
                                fill: vortxColors.gold,
                                fontSize: 10,
                            }}
                        />
                    )}
                    {peakDemand && (
                        <ReferenceLine
                            y={peakDemand}
                            stroke="#ff6b6b"
                            strokeDasharray="3 3"
                        />
                    )}
                    <Area
                        type="monotone"
                        dataKey="load"
                        stroke={vortxColors.teal}
                        strokeWidth={2}
                        fill="url(#evLoadGradient)"
                    />
                </AreaChart>
            </ResponsiveContainer>
        </Box>
    );
}

export default EVLoadChart;
